import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {DepartmentService} from '@app/modules/shared/services/department.service';
import {
	ENewSalaryExportActions,
	FinishExport,
	FinishExportSuccess,
	GetDepartments,
	GetDepartmentsSuccess,
	GetGroups,
	GetGroupsSuccess,
	GetIntegrations,
	GetIntegrationsSuccess,
	GetShiftCalculation,
	GetShiftCalculations,
	GetShiftCalculationsSuccess,
	GetShiftCalculationSuccess,
	GetShiftCount,
	GetShiftCountSuccess,
	SetSelectedDepartment
} from '@app/core/store/actions/new-salary-export.actions';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {Department} from '@app/core/models/department.model';
import {of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {Group} from '@app/core/models/group.model';
import {IntegrationService} from '@app/core/services/api/integration.service';
import {QMIntegration} from '@app/core/models/QMIntegration.model';
import {PayrollService} from '@app/core/services/api/payroll.service';
import {Router} from '@angular/router';
import {GetPayrolls} from '@app/core/store/actions/payrolls.actions';
import {ICalculation, ICalculations} from '@app/core/interfaces/salary';
import {IShiftCount} from '@app/core/interfaces/shiftCount';

@Injectable()
export class NewSalaryExportEffects {

	@Effect()
	public onGetDepartments$ = this.actions$.pipe(
		ofType<GetDepartments>(ENewSalaryExportActions.GetDepartments),
		switchMap((action: GetDepartments) => this.departmentService.getDepartments().pipe(
			switchMap((departments: Department[]) => [
				new GetDepartmentsSuccess(departments),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onSetSelectedDepartment$ = this.actions$.pipe(
		ofType<SetSelectedDepartment>(ENewSalaryExportActions.SetSelectedDepartment),
		map(action => new GetGroups(action.department.data)),
	);

	@Effect()
	public onGetGroups$ = this.actions$.pipe(
		ofType<GetGroups>(ENewSalaryExportActions.GetGroups),
		switchMap((action: GetGroups) => this.departmentService.getGroups(action.department.id).pipe(
			switchMap((groups: Group[]) => [
				new GetGroupsSuccess(groups),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetIntegrations$ = this.actions$.pipe(
		ofType<GetIntegrations>(ENewSalaryExportActions.GetIntegrations),
		switchMap((action: GetIntegrations) => this.integrationService.getIntegrationsForPayroll().pipe(
			switchMap((integrations: QMIntegration[]) => [
				new GetIntegrationsSuccess(integrations),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onFinishExport$ = this.actions$.pipe(
		ofType<FinishExport>(ENewSalaryExportActions.FinishExport),
		switchMap((action: FinishExport) => this.payrollService.createPayroll(action.data).pipe(
			tap(() => this.router.navigate(['./home/salary'])),
			switchMap(() => [
				new FinishExportSuccess(),
				new GetPayrolls(0, 5, ''),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetShiftCount$ = this.actions$.pipe(
		ofType<GetShiftCount>(ENewSalaryExportActions.GetShiftCount),
		switchMap((action: GetShiftCount) => this.payrollService.getShiftCount(action.data).pipe(
			switchMap((shiftCount: IShiftCount) => [
				new GetShiftCountSuccess(shiftCount),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetShiftCalculations$ = this.actions$.pipe(
		ofType<GetShiftCalculations>(ENewSalaryExportActions.GetShiftCalculations),
		switchMap((action: GetShiftCalculations) => this.payrollService.getShiftCalculations(action.year, action.month, action.department_id).pipe(
			switchMap((calculations: ICalculations) => [
				new GetShiftCalculationsSuccess(calculations),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetShiftCalculation$ = this.actions$.pipe(
		ofType<GetShiftCalculation>(ENewSalaryExportActions.GetShiftCalculation),
		switchMap((action: GetShiftCalculation) => this.payrollService.getShiftCalculation(action.year, action.month, action.department_id, action.user_id).pipe(
			switchMap((calculation: ICalculation) => [
				new GetShiftCalculationSuccess(calculation),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private integrationService: IntegrationService,
		private departmentService: DepartmentService,
		private payrollService: PayrollService,
		private actions$: Actions,
		private router: Router
	) {
	}
}
